import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatConnectBalanceLines } from "@/lib/connectBalance";
import { isLitigeRowOpen } from "@/lib/buyerPrivateAccess";
import { toast } from "sonner";

function sellerSaleNet(tx) {
  const n = Number(tx?.seller_net);
  if (Number.isFinite(n) && n >= 0) return n;
  const price = Number(tx?.price) || 0;
  const comm = Number(tx?.commission) || 0;
  return Math.max(0, price - comm);
}

function summarizeSellerPayouts(rows) {
  let pendingEur = 0;
  let disputeHoldEur = 0;
  for (const t of rows || []) {
    if (String(t?.statut || "") !== "fonds_debloques") continue;
    if (t?.stripe_connect_transfer_id && String(t.stripe_connect_transfer_id).trim()) continue;
    const net = sellerSaleNet(t);
    const openLitige = (t?.litiges || []).some(isLitigeRowOpen);
    if (t?.seller_payout_on_hold || openLitige) disputeHoldEur += net;
    else pendingEur += net;
  }
  return { pendingEur, disputeHoldEur };
}

function formatEurCompact(eur) {
  if (!Number.isFinite(eur) || eur < 0.005) return null;
  return `${eur.toLocaleString("fr-FR", {
    minimumFractionDigits: eur < 1 ? 2 : 0,
    maximumFractionDigits: 2,
  })} €`;
}

/** Solde Stripe Connect affiché à 0 alors que des gains validés ne sont pas encore transférés par RdvMarket. */
function stripeAvailableIsZero(amounts) {
  const a = String(amounts?.available || "");
  if (!a || a === "—") return true;
  const n = Number(a.replace(/[^\d,.-]/g, "").replace(",", "."));
  return Number.isFinite(n) && Math.abs(n) < 0.005;
}

/**
 * Solde vendeur Stripe Connect + actions KYC / onboarding (header membre).
 */
export function useMemberSellerWallet() {
  const { user, refresh: refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const kycStatus = user?.kyc_status || "none";
  const identityOk = kycStatus === "approved";

  const [connectStatus, setConnectStatus] = useState(null);
  const [balance, setBalance] = useState(null);
  const [salesPayoutSummary, setSalesPayoutSummary] = useState({ pendingEur: 0, disputeHoldEur: 0 });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [st, txRes] = await Promise.all([
        api.get("/connect/status"),
        api.get("/transactions/mine", { params: { as_role: "seller" } }).catch(() => null),
      ]);
      const data = st?.data || null;
      setConnectStatus(data);
      if (data?.connected && data?.onboarding_complete) {
        const bal = await api.get("/connect/balance");
        const payload = bal?.data ?? bal;
        setBalance(payload?.balance ?? null);
      } else {
        setBalance(null);
      }
      const rawTx = txRes?.data;
      const rows = Array.isArray(rawTx) ? rawTx : Array.isArray(rawTx?.data) ? rawTx.data : [];
      setSalesPayoutSummary(summarizeSellerPayouts(rows));
    } catch {
      setConnectStatus(null);
      setBalance(null);
      setSalesPayoutSummary({ pendingEur: 0, disputeHoldEur: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search || "");
    if (sp.get("identity") !== "done") return;
    void (async () => {
      await refreshUser?.();
      await refresh();
      toast.success("Identité : statut mis à jour.");
    })();
    sp.delete("identity");
    const q = sp.toString();
    navigate({ pathname: location.pathname, search: q ? `?${q}` : "" }, { replace: true });
  }, [location.search, location.pathname, navigate, refresh, refreshUser]);

  const startIdentityVerification = useCallback(async () => {
    try {
      const sp = new URLSearchParams(location.search || "");
      const qs = sp.toString();
      const return_path = `${location.pathname}${qs ? `?${qs}&identity=done` : "?identity=done"}`;
      const { data } = await api.post("/kyc/identity/start", { return_path });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error("Lien indisponible. Activez Stripe Identity sur le compte Stripe de la plateforme.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Erreur");
    }
  }, [location.pathname, location.search]);

  const startConnectOnboarding = useCallback(async () => {
    try {
      const returnPath = location.pathname === "/dashboard" ? `/dashboard?tab=sales` : location.pathname;
      const res = await api.post("/connect/start", { return_path: returnPath });
      const url = res?.data?.url;
      if (!url) throw new Error("Lien de configuration manquant");
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Impossible d’ouvrir la configuration");
    }
  }, [location.pathname]);

  const payoutsReady = Boolean(connectStatus?.connected && connectStatus?.onboarding_complete);

  const phase = useMemo(() => {
    if (loading) return "loading";
    if (!identityOk) return kycStatus === "pending" ? "kyc_pending" : "kyc_required";
    if (!payoutsReady) return "connect_required";
    return "ready";
  }, [loading, identityOk, kycStatus, payoutsReady]);

  const amounts = useMemo(() => formatConnectBalanceLines(balance), [balance]);

  const pendingLabel = formatEurCompact(salesPayoutSummary.pendingEur);
  const disputeLabel = formatEurCompact(salesPayoutSummary.disputeHoldEur);

  const label = useMemo(() => {
    if (phase === "loading") return "Solde…";
    if (phase === "ready") {
      if (disputeLabel) return `${disputeLabel} gelé`;
      if (pendingLabel && stripeAvailableIsZero(amounts)) return `${pendingLabel} à recevoir`;
      return amounts.available;
    }
    if (pendingLabel && (phase === "kyc_required" || phase === "kyc_pending" || phase === "connect_required")) {
      return `${pendingLabel} en attente`;
    }
    if (disputeLabel) return `${disputeLabel} gelé`;
    if (phase === "kyc_pending") return "Vérification identité";
    if (phase === "kyc_required") return "Valider identité";
    if (phase === "connect_required") return "Configurer versements";
    return amounts.available;
  }, [phase, amounts, pendingLabel, disputeLabel]);

  const hint = useMemo(() => {
    const parts = [];
    if (disputeLabel) parts.push(`${disputeLabel} bloqué (litige)`);
    if (phase === "ready") {
      if (pendingLabel && stripeAvailableIsZero(amounts)) {
        parts.push("Virement RdvMarket → Stripe après délai 72 h");
      } else if (amounts.pending !== "—") {
        parts.push(`${amounts.pending} en transit Stripe`);
      }
    }
    if (phase === "kyc_required") parts.push("Identité + IBAN pour être payé");
    else if (phase === "kyc_pending") parts.push("Traitement Stripe");
    else if (phase === "connect_required" && pendingLabel) parts.push("Compléter l’IBAN");
    else if (phase === "connect_required") parts.push("IBAN pour recevoir vos gains");
    if (!parts.length) return null;
    return parts.join(" · ");
  }, [phase, amounts, pendingLabel, disputeLabel]);

  const handleClick = useCallback(() => {
    if (phase === "kyc_required" || phase === "kyc_pending") {
      void startIdentityVerification();
      return;
    }
    if (phase === "connect_required") {
      void startConnectOnboarding();
      return;
    }
    if (phase === "ready") {
      navigate("/dashboard?tab=sales");
    }
  }, [phase, startIdentityVerification, startConnectOnboarding, navigate]);

  return {
    phase,
    label,
    hint,
    amounts,
    payoutsReady,
    pendingEur: salesPayoutSummary.pendingEur,
    disputeHoldEur: salesPayoutSummary.disputeHoldEur,
    loading,
    handleClick,
    refresh,
  };
}
